<template>
  <div class="chart-page">
    <div class="chart-toolbar">
      <div class="toolbar-row">
        <pods-input ref="podsInput" />
        <div class="toolbar-actions">
          <el-button
            size="small"
            type="primary"
            plain
            @click="streamPodData()"
            :loading="streamLoading"
            >Stream</el-button
          >
          <el-button
            v-if="p('Pods.Capacitance Readings Chart.Export')"
            size="small"
            type="primary"
            plain
            @click="exportData()"
            :loading="downloadLoading"
            >{{ $t('text.export') }}</el-button
          >
          <PadCycleFilters :showPadCycleIdFilter="true" />
        </div>
      </div>
      <div class="toolbar-row">
        <DatePicker />
      </div>
    </div>

    <div class="app-card" v-loading="loading">
      <div id="echarts" :style="{ width: '100%', height: '400px' }"></div>
      <TagBox
        ref="tagBox"
        :fromDate="fromDate"
        :toDate="toDate"
        :podMac="this.$store.state.pods.selectedPod.podId"
        :utcOffset="this.$store.state.pods.selectedUtcOffset"
        @savedSuccess="handleAfterSavedTag()"
        v-if="showTagBox"
      />
      <StreamPodData ref="streamPodData" />
    </div>
  </div>
</template>

<script>
import PodsInput from '@/components/PodsInput'
import DatePicker from '@/components/DatePicker'
import TagBox from '@/components/TagBox'
import swal from 'sweetalert'
import ApiService from '@/services/ApiService.js'
import CommonApiService from '@/services/CommonApiService.js'
import moment from 'moment-timezone'
import { converFile } from '../../../utils/utils'
import StreamPodData from '@/components/pods/chartData/StreamPodData'
import PadCycleFilters from '@/components/pods/chartData/PadCycleFilters'
export default {
  data() {
    return {
      showTagBox: false,
      chart: '',
      fromDate: '',
      toDate: '',
      downloadLoading: false,
      streamLoading: false,
      loading: false,
      source: []
    }
  },
  props: {
    tooltip: {
      require: true
    }
  },
  components: {
    PodsInput,
    DatePicker,
    TagBox,
    StreamPodData,
    PadCycleFilters
  },
  mounted() {
    this.getData()
  },
  watch: {
    '$store.state.pods.selectedPod': function() {
      this.getData()
    },
    '$store.state.pods.selectedDate': function() {
      this.getData()
    },
    '$store.state.pods.selectedUtcOffset': function() {
      this.getData()
    }
  },
  methods: {
    getData() {
      if (!this.$store.state.pods.selectedPod.podId) return false
      this.loading = true
      CommonApiService.GetCapacitanceReadings(
        this.$store.state.pods.selectedPod.podId,
        this.$store.state.pods.selectedDate[0],
        this.$store.state.pods.selectedDate[1],
        moment.tz(this.$store.state.pods.selectedUtcOffset).utcOffset() / 60
      )
        .then(response => {
          this.loading = false
          if (response.data.success) {
            this.source = [
              ['timeStampWithUtc', 'sensor1Value', 'sensor2Value', 'timeStamp']
            ]
            response.data.data.data.forEach(item => {
              this.source.push([
                moment
                  .utc(item.timeStamp)
                  .tz(this.$store.state.pods.selectedUtcOffset)
                  .format('YYYY-MM-DD HH:mm:ss'),
                item.sensor1Value,
                item.sensor2Value,
                item.timeStamp
              ])
            })
            if (this.source.length <= 1) {
              this.source.push(['', 0, 0])
            }
            this.initChart()
          } else {
            swal({
              icon: 'error',
              timer: 2000,
              title: this.$t('text.failed'),
              text: response.data.message
            })
          }
        })
        .catch(() => {
          this.loading = false
          swal({
            icon: 'error',
            timer: 2000,
            title: this.$t('text.failed')
          })
        })
    },
    firstLetterCapitalize(val) {
      return val.charAt(0).toUpperCase() + val.slice(1)
    },
    initChart() {
      let vm = this
      var dom = document.getElementById('echarts')
      this.chart = this.echarts.init(dom)
      this.chart.setOption({
        grid: {
          left: 0,
          right: 20,
          bottom: 50,
          top: 30,
          containLabel: true
        },
        toolbox: {
          show: this.source[1] && this.source[1][0],
          feature: {
            brush: {
              title: {
                lineX: this.$t('text.select'),
                clear: this.$t('text.clear_selected')
              }
            }
          }
        },
        tooltip: this.tooltip,
        color: ['#008ffb', '#00e396'],
        legend: {
          x: 'center',
          y: 'bottom',
          bottom: 0,
          data: [this.$t('text.sensor') + ' 1', this.$t('text.sensor') + ' 2']
        },
        dataZoom: [
          {
            show: true,
            height: 25,
            bottom: 25,
            start: 0,
            zoomOnMouseWheel: true,
            moveOnMouseMove: true
          },
          {
            type: 'inside'
          }
        ],
        dataset: {
          dimensions: ['timeStampWithUtc', 'sensor1Value', 'sensor2Value'],
          source: this.source
        },
        xAxis: [
          {
            type: 'time',
            boundaryGap: false,
            splitLine: {
              show: false
            },
            axisLabel: {
              show: true,
              showMinLabel: true,
              showMaxLabel: true
            }
          }
        ],
        yAxis: [
          {
            type: 'value',
            nameLocation: 'center',
            nameGap: 40,
            axisLine: {
              show: false
            },
            splitLine: {
              show: true,
              lineStyle: {
                color: ['#f2f2f2'],
                width: 1,
                type: 'solid'
              }
            }
          }
        ],
        series: [
          {
            name: this.$t('text.sensor') + ' 1',
            type: 'line',
            symbol: 'circle',
            smooth: true,
            encode: {
              x: 'timeStampWithUtc',
              y: 'sensor1Value'
            }
          },
          {
            name: this.$t('text.sensor') + ' 2',
            type: 'line',
            symbol: 'circle',
            smooth: true,
            encode: {
              x: 'timeStampWithUtc',
              y: 'sensor2Value'
            }
          }
        ],
        brush: {
          toolbox: ['lineX', 'clear'],
          xAxisIndex: 0,
          throttleType: 'debounce',
          throttleDelay: 200,
          brushStyle: {
            borderWidth: 2,
            color: 'rgba(255, 255, 0, 0.15)',
            borderColor: '#ec808d'
          }
        }
      })
      this.chart.off('brushSelected')
      this.chart.on('brushSelected', this.brushSelected)
      this.chart.on('brushEnd', this.brushEnd)
      window.addEventListener('resize', function() {
        vm.chart.resize()
      })
    },
    brushSelected(params) {
      if (this.showTagBox && !params.batch[0].areas.length) {
        this.clearTagForm()
      }
    },
    clearTagForm() {
      this.showTagBox = false
      this.$refs.tagBox.$refs.tagForm.resetFields()
      this.$refs.tagBox.tagDataForm = {
        tag: [],
        notes: ''
      }
      this.fromDate = this.toDate = ''
    },
    brushEnd(params) {
      this.showTagBox = true

      //Gets the index of the selected region
      let tagSelectedArea = params.areas[0].range
      let seleted = this.chart._chartsViews[0]._points
        .map(([left], i) => {
          return left >= tagSelectedArea[0] && left <= tagSelectedArea[1]
            ? i
            : -1
        })
        .filter(item => {
          return item >= 0
        })

      //Gets the data for the visual region after scaling
      let shownSource = []
      let rangeStart = this.chart.getModel().option.xAxis[0].rangeStart
      let rangeEnd = this.chart.getModel().option.xAxis[0].rangeEnd
      this.source.forEach((item, index) => {
        if (index > 0) {
          if (rangeStart && rangeEnd) {
            if (
              moment(item[0]).valueOf() >= rangeStart &&
              moment(item[0]).valueOf() <= rangeEnd
            ) {
              shownSource.push(item)
            }
          } else {
            shownSource.push(item)
          }
        }
      })

      this.fromDate = shownSource[seleted[0]][3]
      this.toDate = shownSource[seleted[seleted.length - 1]][3]
    },
    handleAfterSavedTag() {
      this.clearTagForm()
      this.chart.dispatchAction({
        type: 'brush',
        areas: []
      })
    },
    exportData() {
      this.downloadLoading = true
      CommonApiService.DownloadRawData(
        this.$store.state.pods.selectedPod.podId,
        moment(this.$store.state.pods.selectedDate[0]).format(
          'YYYY-MM-DD HH:mm:ss'
        ),
        moment(this.$store.state.pods.selectedDate[1]).format(
          'YYYY-MM-DD HH:mm:ss'
        ),
        moment.tz(this.$store.state.pods.selectedUtcOffset).utcOffset() / 60
      )
        .then(response => {
          this.downloadLoading = false
          if (!response.data) return false
          let reportName =
            this.$store.state.pods.selectedPod.podId +
            ' ' +
            moment(this.$store.state.pods.selectedDate[0]).format(
              'YYYY-MM-DD HH:mm:ss'
            ) +
            ' to ' +
            moment(this.$store.state.pods.selectedDate[1]).format(
              'YYYY-MM-DD HH:mm:ss'
            ) +
            'Pod Data.csv'
          converFile(reportName, response.data)
        })
        .catch(() => {
          this.downloadLoading = false
          swal({
            icon: 'error',
            timer: 2000,
            title: this.$t('text.failed')
          })
        })
    },
    streamPodData() {
      this.streamLoading = true
      ApiService.FetchStreamPodData(
        this.$store.state.pods.selectedPod.podId,
        moment(this.$store.state.pods.selectedDate[0]).format(
          'YYYY-MM-DD HH:mm:ss'
        ),
        moment(this.$store.state.pods.selectedDate[1]).format(
          'YYYY-MM-DD HH:mm:ss'
        ),
        moment.tz(this.$store.state.pods.selectedUtcOffset).utcOffset() / 60
      )
        .then(response => {
          this.streamLoading = false
          if (response.data.length) {
            this.$refs.streamPodData.openDialog(response.data)
          } else {
            swal({
              icon: 'warning',
              timer: 2000,
              title: 'No data was obtained'
            })
          }
        })
        .catch(() => {
          this.streamLoading = false
          swal({
            icon: 'error',
            timer: 2000,
            title: this.$t('text.failed')
          })
        })
    }
  },
  beforeDestroy() {
    if (this.chart) this.chart.clear()
  }
}
</script>
